import { AuthTokenCancelledError } from './authToken';
import { ErrorCode, ServerErrorResponse, ValidationError } from './types';

export class ApiError<T> extends Error {
  private _errors: ValidationError<T> | undefined;
  private _errorCode?: ErrorCode;
  private _status?: number;

  constructor(
    message: string,
    errors?: ValidationError<T>,
    errorCode?: ErrorCode,
    status?: number
  ) {
    super(message);

    this._errors = errors;
    this._errorCode = errorCode;
    this._status = status;
  }

  get errors(): ValidationError<T> | undefined {
    return this._errors;
  }

  get errorCode(): string | undefined {
    return this._errorCode;
  }

  get status(): number | undefined {
    return this._status;
  }

  static noResponse: ApiError<unknown> = new ApiError('No response from the server');
  static timeout: ApiError<unknown> = new ApiError('The request timed out', undefined, 'unknown', 408);

  static fromResponse<T>(status: number | undefined, data: ServerErrorResponse<T>): ApiError<T> {
    if (data?.message) {
      return new ApiError(
        data.message,
        data.errors,
        data.code ?? ApiError.getErrorCode(status),
        status
      );
    }

    switch (status) {
      case 401:
        return new ApiError('Unauthorized', undefined, ApiError.getErrorCode(status), status);

      case 403:
        return new ApiError('Forbidden', undefined, ApiError.getErrorCode(status), status);

      case 404:
        return new ApiError('Not found', undefined, ApiError.getErrorCode(status), status);

      default:
        return new ApiError('API unknown error', undefined, ApiError.getErrorCode(status), status);
    }
  }

  static getErrorCode(status?: number): ErrorCode {
    switch (status) {
      case 401:
        return 'unauthorized';

      case 403:
        return 'forbidden';

      case 404:
        return 'not_found';

      default:
        return 'unknown';
    }
  }

  static fromMessage(message: string): ApiError<unknown> {
    return new ApiError(message);
  }

  static fromError(error: AuthTokenCancelledError): ApiError<unknown> {
    return new ApiError(error.message, undefined, error.errorCode);
  }
}
